import { Flex, Link } from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import { collection, getDocs, getDoc, doc } from "firebase/firestore";
import { useNavigate, useLocation } from "react-router-dom";
import Footer from "../chat/Footer";
import Messages from "../chat/Messages";
import NavBar from "../NavBar";
import axios from "axios";
import { db } from "../../firebase";
import { Link as ReactRouterLink } from "react-router-dom";
import { useAccount } from "../../lib/accountContext";
import { useRedirectToLogin } from "../../lib/hooks";
import fetchWithToken from "../../fetchWithToken";
import toast, { Toaster } from 'react-hot-toast';

const PDFChat = () => {
    const account = useAccount();

    const [chatData, setChatData] = useState({});
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState("");
    const [shouldScroll, setShouldScroll] = useState(false);
    const [sources, setSources] = useState([]);
    const [showCancelButton, setShowCancelButton] = useState(false);
    const [isFeatured, setIsFeatured] = useState(true);

    const abortController = new AbortController();
    const signal = abortController.signal;

    const location = useLocation();
    const navigate = useNavigate();
    const pathName = location.pathname;

    useRedirectToLogin();

    // Sets isSubscribed to true for everyone if ENABLE_PAYWALL is set to false
    const ENABLE_PAYWALL = false;
    const isSubscribed = ENABLE_PAYWALL ? account.isSubscribed : true;

    if (pathName === "/chats/robin-free") {
        navigate("/chats/robin");
    }

    const deleteChatbot = async () => {
        const requestOptions = {
            method: "DELETE",
            headers: { "Content-Type": "application/json", ...axios.defaults.headers },
            body: JSON.stringify({
                path: chatData.path
            }),
        };

        try {
            const response = await toast.promise(
                fetchWithToken('/api/chat', requestOptions).then((res) => {
                    if (!res.ok) {
                        throw new Error("Request failed with status " + res.status);
                    }
                    return res;
                }),
                {
                    loading: 'Deleting Chat',
                    success: 'We removed the chat for you 🤝',
                    error: 'Sorry we had trouble deleting the chat.',
                }
            );

            const responseData = await response.json();
            navigate("/chats");
        } catch (error) {
            console.error('Error:', error);
            toast.error("Sorry, we had trouble deleting the chat");
        }
    }

    useEffect(() => {
        // Set Initial Message
        const fetchData = async () => {
            const featuredChatsPromise = getDocs(collection(db, "featuredChats"));
            const userChatsPromise = getDoc(doc(db, "users", account.user.uid));

            const [featuredChatsSnapshot, userDoc] = await Promise.all([
                featuredChatsPromise,
                userChatsPromise,
            ]);

            const featuredChats = featuredChatsSnapshot.docs.map((doc) => ({
                path: doc.id,
                ...doc.data(),
            }));

            const userChats = userDoc.exists ? userDoc.data().chats || [] : [];

            // Combine user chats and featured chats
            const allChats = [...userChats, ...featuredChats];

            const chatByPath = allChats.find((chat) => "/chats/" + chat.path === pathName);

            // Redirect if no matching chat is found
            if (!chatByPath) {
                navigate("/chats");
            }

            setChatData(chatByPath);
            setMessages([
                { role: "assistant", content: chatByPath?.initial_message || "" },
            ]);
            setInputMessage("");
        };

        if (account.user) {
            fetchData();
            localStorage.setItem("stop", null);
        }
    }, [pathName, account.user, navigate]);

    async function getSources(newMessages) {
        try {
            const contextResponse = await fetchWithToken("/api/query", {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    message: inputMessage,
                    namespace: chatData.namespace || chatData.path,
                    k: chatData.path === 'florida-statutes' ? 5 : 3, // florida-statutes gets more context, since smaller embeddings
                })
            });
            const sourcesData = await contextResponse.json();

            // combine each content source into it 
            let context = "";
            sourcesData.forEach(source => {
                context += source.content + "\n\n"
            })

            const chatResponse = await fetchWithToken(axios.defaults.baseURL + "/pdf-chat", {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...axios.defaults.headers,
                },
                body: JSON.stringify({
                    messages: newMessages,
                    context: context
                }),
                signal: signal,
            });

            if (!chatResponse.ok) {
                setShouldScroll(false);
                toast.error("Sorry, there is a problem. We are working on it!");
                return;
            };

            const chatdb_response = chatResponse.body;
            if (!chatdb_response) {
                setShouldScroll(false);
                return;
            }

            setSources(sourcesData);

            const reader = chatdb_response.getReader();
            const decoder = new TextDecoder();

            let done = false;
            setMessages((old) => [...old, { role: "assistant", content: "" }]);
            setShowCancelButton(true);

            while (!done) {
                if (localStorage.getItem('stop') !== 'stop') {

                    const { value, done: doneReading } = await reader.read();
                    done = doneReading;
                    const chunkValue = decoder.decode(value);

                    if (chunkValue !== undefined) {
                        setMessages((oldMessages) => {
                            const lastMessage = oldMessages[oldMessages.length - 1];
                            const updatedLastMessage = {
                                ...lastMessage,
                                content: lastMessage.content + chunkValue,
                            };
                            return [...oldMessages.slice(0, -1), updatedLastMessage];
                        });
                    }

                } else {
                    done = true;
                    abortController.abort(); // Cancel the request
                }
            }
            localStorage.setItem('stop', null);
            setShowCancelButton(false);
            setShouldScroll(false);
        } catch (error) {
            console.error(error);
            setShouldScroll(false);
            toast.error("Sorry this didn't work. Try refreshing the page and trying again.")
            return;
        };
        setShouldScroll(false);
    }

    const handleSendMessage = async () => {
        setSources([]);
        if (!inputMessage.trim().length || !inputMessage.trim().length === 0) {
            return;
        }

        setShouldScroll(true);

        const data = inputMessage;
        const newMessages = [...messages, { role: "user", content: data }];
        setMessages(newMessages);
        setInputMessage("");
        getSources(newMessages);
    }

    const clickCancelButton = () => {
        setShowCancelButton(false);
        localStorage.setItem('stop', 'stop');
    }

    return (
        <div>
            <NavBar />
            <div className="text-sm flex justify-between items-center w-full">
                <div className="ml-10 breadcrumbs">
                    <ul>
                        <li>
                            <Link as={ReactRouterLink} to="/chats" style={{ textDecoration: "none" }}>
                                Chats
                            </Link>
                        </li>
                        <li><a>{chatData.title}</a></li>
                    </ul>
                </div>
                {/* Add the options button here */}
                <div className="dropdown dropdown-bottom dropdown-end">
                    <label tabIndex={0} className="btn btn-ghost m-1">Options</label>
                    <ul tabIndex={0} className="dropdown-content menu p-2 shadow bg-base-100 rounded-box w-52">
                        <li><label htmlFor="my-modal-4" className="btn btn-ghost">Delete Chat</label></li>
                    </ul>
                </div>
            </div>

            <Flex w="100%" h="85vh" flexDir="column">
                <Messages
                    messages={messages}
                    topics={chatData?.options?.topics || []}
                    shouldScroll={shouldScroll}
                    sources={sources}
                    title={chatData.title}
                />
                {showCancelButton ?
                    (<div className="text-center">
                        <button className="rounded-full bg-red-500 w-32 h-8 mt-5 text-white" onClick={() => clickCancelButton()}>
                            <span className="inline-block w-3 h-3 bg-white rounded-sm mr-2"></span>
                            Stop
                        </button>
                    </div>) : ''
                }
                <Footer
                    shouldScroll={shouldScroll}
                    inputMessage={inputMessage}
                    setInputMessage={setInputMessage}
                    handleSendMessage={handleSendMessage}
                />
            </Flex>
            <input type="checkbox" id="my-modal-4" className="modal-toggle" />
            <label htmlFor="my-modal-4" className="modal cursor-pointer">
                <label className="modal-box relative" htmlFor="">
                    <h3 className="text-xl font-bold">Are you sure?</h3>
                    <p className="py-4">Are you sure you want to delete your chatbot? This action cannot be undone.</p>
                    <div className="modal-action">
                        <label htmlFor="my-modal-4" className="btn">Cancel</label>
                        <label onClick={deleteChatbot} htmlFor="my-modal-4" className="btn btn-error">Delete</label>
                    </div>
                </label>
            </label>
            <Toaster
                position="top-center"
                reverseOrder={false}
            />
        </div>
    );
}

export default PDFChat;